import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  View,
  useAppContext,
  useClientContext,
  useNavigation,
} from "@data-root/react";
import {
  ArrowLeftOutlined,
  MenuOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Layout, Menu, MenuProps, Space, Typography } from "antd";
import { ErrorView } from "@data-root/error-boundary";
import { DesktopNavigationConfig } from "./desktop";
import { useAntdMenu } from "./menu";
import { useParams } from "react-router";
import * as Sentry from "@sentry/react";
import { getEmbedModeFlag, getShowNavigationFlag } from "@/utils/tokenManager";

const { Header, Content } = Layout;
const { Text } = Typography;

export const MobileNavigatorComponent: FC<
  PropsWithChildren<DesktopNavigationConfig>
> = ({
  children,
  headerLeftItems,
  headerCenterItems,
  headerRightItems,
  menu,
}) => {
    const { navigation } = useNavigation();
    const { viewId } = useParams();

    const { core, user } = useClientContext();
    const appContext = useAppContext();
    const { menu: _menu } = appContext;

    const [open, setOpen] = useState(false);

    const openDrawer = useCallback(() => setOpen(true), []);

    const closeDrawer = useCallback(() => setOpen(false), []);

    const onClickRoute = useCallback(
      (viewId: string) => {
        navigation.navigate(viewId);
        closeDrawer();
      },
      [closeDrawer, navigation]
    );

    const { antdMenu } = useAntdMenu(onClickRoute);

    const onClickBack = useCallback(() => {
      // eslint-disable-next-line prefer-const
      let prevent = false;
      core.emitter.emit("back", core, () => (prevent = true));
      if (prevent) return;

      navigation.goBack();
    }, [core, navigation]);

    const items = useMemo<MenuProps["items"]>(() => {
      return antdMenu;
    }, [antdMenu]);

    const stylesheet = useMemo(
      () => ({
        header: {
          paddingInline: 20,
          backgroundColor: appContext.styles.primaryColor,
        },
        headerSpacer: {
          width: "100%",
          justifyContent: "space-between",
        },
        drawer: {
          backgroundColor: "#ffffff",
        },
        drawerItem: {
          backgroundColor: appContext.styles.lightTintColor,
          color: appContext.styles.darkTintColor,
        },
        buttonPrimary: {
          color: appContext.styles.primaryColor,
        },
      }),
      [appContext.styles]
    );

    const isEmbedMode = useMemo(() => getEmbedModeFlag(), []);
    const showNavigationInEmbed = useMemo(() => getShowNavigationFlag(), []);

    if (isEmbedMode) {
      if (showNavigationInEmbed) {
        return (
          <Layout style={style}>
            <Content style={{ overflow: "auto", padding: 10 }}>
              <Button 
                type="link" 
                icon={<MenuOutlined />} 
                onClick={openDrawer} 
                style={{ marginBottom: 10 }}
              />
              <Sentry.ErrorBoundary fallback={ErrorView as any}>
                {children}
              </Sentry.ErrorBoundary>
            </Content>
            <Drawer
              style={stylesheet.drawer}
              placement="left"
              getContainer={false}
              open={open}
              onClose={closeDrawer}
            >
              <Menu
                selectedKeys={[viewId]}
                onClick={({ key }) => onClickRoute(key)}
                items={items}
                mode="inline"
              />
            </Drawer>
          </Layout>
        );
      }
      return (
        <Layout style={style}>
          <Content style={{ overflow: "auto", padding: 10 }}>
            <Sentry.ErrorBoundary fallback={ErrorView as any}>
              {children}
            </Sentry.ErrorBoundary>
          </Content>
        </Layout>
      );
    }

    return (
      <Layout style={style}>
        <Header style={stylesheet.header}>
          <Space
            style={stylesheet.headerSpacer}
            direction="horizontal"
            align="center"
          >
            <Space>
              <Button type="link" icon={<MenuOutlined />} onClick={openDrawer} />

              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={onClickBack}
                disabled={!navigation.canGoBack()}
              />

              {headerLeftItems}
            </Space>

            <View>{headerCenterItems}</View>

            <Space
              styles={{
                item: {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {headerRightItems}
            </Space>
          </Space>
        </Header>

        <Content style={{ overflow: "auto", padding: 10 }}>
          <Sentry.ErrorBoundary fallback={ErrorView as any}>
            {children}
          </Sentry.ErrorBoundary>
        </Content>

        <Drawer
          style={stylesheet.drawer}
          placement="left"
          getContainer={false}
          open={open}
          onClose={closeDrawer}
          extra={
            <Space>
              <Text>
                Versión: <Text code copyable>{__GIT_HASH__}</Text>
              </Text>

              {/* <Button
                style={stylesheet.buttonPrimary}
                type="link"
                icon={<UserOutlined />}
              >
                {user?.name}
              </Button> */}
            </Space>
          }
          footer={
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Space direction="horizontal">
                {menu.map(({ key, label, icon, onClick }) => (
                  <Button
                    key={key}
                    style={stylesheet.buttonPrimary}
                    type="link"
                    icon={icon}
                    onClick={onClick}
                  >
                    {label}
                  </Button>
                ))}
              </Space>
            </div>
          }
        >
          <Menu
            selectedKeys={[viewId]}
            onClick={({ key }) => onClickRoute(key)}
            items={items}
            mode="inline"
          />
        </Drawer>
      </Layout>
    );
  };

const style = {
  maxWidth: "100vw",
};
